import React from 'react';
import DronesMap from './drones';
import { useDispatch, useSelector } from 'react-redux';
import { ArcBaseContent } from '@src/core/components';
import { ArcGoogleMap } from '@src/core/controllers';
import { useDroneProvider } from '@src/core/features';
import { useGoHere } from '@src/features/drone/context/go-here-context';
import { trackError } from '@src/core/sentry';
import { getGeocode } from 'use-places-autocomplete';
import { setMapLocation } from '@src/features/drone/redux/actions';
import { selectSelectedDrone } from '@src/features/drone/redux/selectors';
import ZonesControl from '@src/core/features/zones/components/zones-control';
import { SearchLocation } from '@src/features/drone/components/search-location';
import { MapStatusBanners } from '@src/features/drone/components/map-status-banners';
import { useCameraLayout } from '@src/features/order/components/camera-view/camera-layout-context';
import CameraMapLayout from '@src/features/order/components/camera-view/camera-map-layout';
import { useFlightProvider } from '@src/features/order/provider';

const Maps: React.FC = () => { 
  const { goTo, position, points, removePosition, addPosition, resetGoHere } = useGoHere();
  const { viewMode, cameraSource } = useCameraLayout();
  const dispatch = useDispatch();
  const selectedFromRedux = useSelector(selectSelectedDrone);
  const { drones } = useDroneProvider();
  const { gpsInfo, flightData } = useFlightProvider();

  const selectedSerial = selectedFromRedux?.serialNumber;
  const selectedDrone = React.useMemo(
    () =>
      selectedSerial
        ? drones.find((drone) => drone.serialNumber === selectedSerial) ?? selectedFromRedux
        : null,
    [drones, selectedFromRedux, selectedSerial],
  );

  const gpsBelongsToSelected =
    Boolean(selectedSerial) && flightData?.serialNumber === selectedSerial;
  const gpsLost = Boolean(gpsBelongsToSelected && gpsInfo?.gps_status?.gpsLost);
  const connectionLost = Boolean(selectedDrone?.connectionLost);

  // Default → map. Swap → camera. Split → one full-width strip on layout.
  const showBannersOnMap =
    !(!!cameraSource && (viewMode === 'pip' || viewMode === 'split'));
  const statusBanners = (
    <MapStatusBanners gpsLost={gpsLost} connectionLost={connectionLost} />
  );

  const onRightClick = async (event: google.maps.MapMouseEvent) => {  
    if (!event.latLng) return;
    
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    if (!lat || !lng) return;

    let address = '';
    try {
      const results = await getGeocode({ location: { lat, lng } });
      address = results[0]?.formatted_address ?? '';
    } catch (error) {
      console.error("Geocode error:", error);
      trackError(error instanceof Error ? error : new Error(String(error)), {
        code: 'FE_0007',
        extra: { lat, lng },
      });
    }

    // Show overlay only after geocode so address and coords appear together (no stale or empty flash).
    dispatch(setMapLocation({ latitude: lat, longitude: lng, address }));
    addPosition({ lat, lng });
  };

  return (
    <ArcBaseContent>
      <CameraMapLayout statusBanners={statusBanners}>
        <ArcGoogleMap onRightClick={onRightClick}>
          <ZonesControl />
          <SearchLocation addPosition={addPosition}/>
          {showBannersOnMap && statusBanners}
          <DronesMap
            goTo={goTo}
            position={position}
            removePosition={removePosition}
            addPosition={addPosition}
            resetGoHere={resetGoHere}
            points={points}
          />
        </ArcGoogleMap>
      </CameraMapLayout>
    </ArcBaseContent>
  );
};

export default Maps;
